/* O8 Gut Ritual bundle interaction.
 *
 * On tile click: writes quantity (= bottle count) + selling_plan together
 * (lockstep), updates the ATC button price and the below-ATC dynamic copy.
 * Subscription-only — there is no one-time path to sync.
 *
 * On submit: routes through the shared, product-agnostic SubCartStack module
 * (assets/sub-cart-stack.js) — the same module O1-O4 already rely on — so
 * adding "3 bottles" then "2 bottles" merges into one line under a single
 * subscription (snapped up to whichever plan covers >=5 bottles) instead of
 * two parallel ones. After a successful add, explicitly opens UpCart via its
 * public window.NuoraUpcart.open() hook — a safe, redundant trigger alongside
 * UpCart's own fetch intercept.
 */
(() => {
  const ROOT_SELECTOR = '[data-o8-bundle]';

  // Parse the server-emitted full backend ladder "qty:planId,qty:planId,..."
  // (data-full-ladder) into [{qty, planId}].
  function parseFullLadder(str) {
    return (str || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((pair) => {
        const idx = pair.indexOf(':');
        return { qty: parseInt(pair.slice(0, idx), 10), planId: String(pair.slice(idx + 1)) };
      })
      .filter((e) => e.qty > 0 && e.planId);
  }

  function init(root) {
    if (root.dataset.o8BundleInit) return;
    root.dataset.o8BundleInit = '1';

    const tiles = [...root.querySelectorAll('.o8-bundle__bar')];
    const qtyInput = root.querySelector('[data-o8-bundle-qty]');
    const planInput = root.querySelector('[data-o8-bundle-plan]');
    if (!tiles.length || !qtyInput || !planInput) return;

    // `root` (matched by [data-o8-bundle]) IS the <form> — the snippet
    // renders its own Shopify {% form 'product' %} so this never depends on
    // an external buy_buttons form existing elsewhere on the page.
    const mainForm = root;
    const atcBtn = root.querySelector('[data-o8-bundle-atc]');
    const atcStateEls = atcBtn ? [...atcBtn.querySelectorAll('[data-o8-bundle-atc-state]')] : [];
    let addedTimer = null;

    // Swaps which of the idle/"Adding to Cart…"/"Added to Cart" spans is visible.
    function setAtcState(state) {
      atcStateEls.forEach((el) => {
        el.hidden = el.dataset.o8BundleAtcState !== state;
      });
    }

    const rebillEl = root.querySelector('[data-o8-bundle-rebill]');
    const freeshipEl = root.querySelector('[data-o8-bundle-freeship]');

    function select(tile) {
      const qty = parseInt(tile.dataset.qty, 10);
      qtyInput.value = String(qty);
      planInput.value = tile.dataset.plan;
      tiles.forEach((t) => t.setAttribute('aria-checked', t === tile ? 'true' : 'false'));
      if (rebillEl) rebillEl.textContent = tile.dataset.rebillLine || '';
      if (freeshipEl) freeshipEl.hidden = tile.dataset.freeship !== 'true';
    }

    tiles.forEach((tile) => tile.addEventListener('click', () => select(tile)));

    // --- Cart stacking: one subscription line for this offer. -------------
    const tileTiers = tiles.map((t) => ({ qty: parseInt(t.dataset.qty, 10), planId: String(t.dataset.plan) }));
    const ladderTiers = parseFullLadder(root.dataset.fullLadder);
    const built =
      window.SubCartStack && window.SubCartStack.buildOfferTiers
        ? window.SubCartStack.buildOfferTiers(tileTiers, ladderTiers)
        : { tiers: tileTiers, planIds: tileTiers.map((e) => e.planId) };
    const tierList = built.tiers;
    const myPlanIdSet = new Set(built.planIds);
    let atcBusy = false;

    function setLoading(on) {
      if (!atcBtn) return;
      if (on) atcBtn.setAttribute('aria-disabled', 'true');
      else atcBtn.removeAttribute('aria-disabled');
      const spinner = atcBtn.querySelector('.o8-bundle__spinner');
      if (spinner) spinner.hidden = !on;
    }

    function formVal(form, name) {
      try { return new FormData(form).get(name); } catch (e) { return null; }
    }

    function showAtcError(form) {
      const wrap = form.querySelector('.product-form__error-message-wrapper');
      const msg = wrap && wrap.querySelector('.product-form__error-message');
      if (wrap && msg) {
        msg.textContent = "Sorry — we couldn't update your cart. Please try again.";
        wrap.removeAttribute('hidden');
      }
    }

    async function runConsolidation(form) {
      if (atcBusy || !window.SubCartStack) return;
      const selectedQty = parseInt(formVal(form, 'quantity'), 10) || 1;
      const variantId = formVal(form, 'id');
      if (!variantId) {
        console.error('[o8-bundle] no variant id — aborting consolidation');
        return;
      }
      atcBusy = true;
      clearTimeout(addedTimer);
      setLoading(true);
      setAtcState('loading');
      try {
        const { addResponse } = await window.SubCartStack.consolidateSubscriptionAdd({
          planIds: [...myPlanIdSet],
          tiers: tierList,
          variantId: variantId,
          selectedQty: selectedQty,
        });
        // UpCart's own fetch intercept will normally pick up the /cart/add.js
        // calls above; this is a redundant, explicit trigger so the drawer
        // always opens regardless of that timing.
        if (window.NuoraUpcart && typeof window.NuoraUpcart.open === 'function') {
          window.NuoraUpcart.open();
        }
        void addResponse;
        // Hide the spinner BEFORE showing "Added to Cart" — don't rely on
        // both mutations landing in the same paint frame.
        setLoading(false);
        setAtcState('added');
        addedTimer = setTimeout(() => setAtcState('idle'), 1500);
      } catch (err) {
        console.error('[o8-bundle] cart consolidation failed', err);
        showAtcError(form);
        setLoading(false);
        setAtcState('idle');
      } finally {
        atcBusy = false;
      }
    }

    function onFormSubmitCapture(evt) {
      const form = evt.currentTarget;
      const planVal = String(formVal(form, 'selling_plan') || '');
      if (!planVal || !myPlanIdSet.has(planVal)) return;
      evt.preventDefault();
      evt.stopImmediatePropagation();
      runConsolidation(form);
    }

    if (mainForm) mainForm.addEventListener('submit', onFormSubmitCapture, true);

    // Initialise everything from the server-preselected tile (no flash).
    const selected =
      tiles.find((t) => t.getAttribute('aria-checked') === 'true') || tiles[tiles.length - 1];
    if (selected) select(selected);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
